import React, {useEffect, useMemo, useState} from "react";
import Slide from "@mui/material/Slide";
import useTriStateSort from "../hooks/useTriStateSort";
import PageHeader from "../components/PageHeader";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SearchField from "../components/SearchField";
import SuccessDialog from "../components/SuccessDialog";
import {formatToLocalTime} from "../utils/dateUtils";
import {handleGraphQLError} from "../utils/graphqlErrorHandler";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {gql, useMutation, useQuery} from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EllipsisCell from "../components/EllipsisCell";
import TablePaginationActions from "../components/TablePaginationActions";

import {useTranslation} from "react-i18next";

const SlideUp = (props) => <Slide {...props} direction="up"/>;


const PAGINATE_USERS = gql`
    query PaginateUsers(
        $pagination: Pagination!
        $sortBy: [SortBy!]
        $criteria: UserCriteria
    ) {
        paginateUser(
            pagination: $pagination
            sortBy: $sortBy
            criteria: $criteria
        ) {
            pageInfo {
                totalElements
                totalPages
                numberOfElements
                pageNumber
                pageSize
            }
            content {
                id
                email
                firstName
                lastName
                updatedTimestamp
                deleted
                master
            }
        }
    }
`;

const CREATE_USER = gql`
    mutation CreateUser($input: UserInput!) {
        createUser(input: $input) {
            id
            firstName
            lastName
            email
            defaultPassword
        }
    }
`;

const UPDATE_USER = gql`
    mutation UpdateUser($id: ID!, $input: UserInput!) {
        updateUser(id: $id, input: $input) {
            id
            firstName
            lastName
            email
        }
    }
`;

const DELETE_USER = gql`
    mutation DeleteUser($id: ID!) {
        deleteUser(id: $id)
    }
`;


const Users = () => {
    const {t} = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);


    const {sortFields, handleSort, getSortProps} = useTriStateSort(
        (updated) => {
            refetch({...variables, sortBy: updated});
        }
    );


    const [createdUserInfo, setCreatedUserInfo] = useState(null);
    const [nameFilter, setNameFilter] = useState("");
    const [emailFilter, setEmailFilter] = useState("");
    const [debouncedFilters, setDebouncedFilters] = useState({name: "", email: ""});


    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const [createForm, setCreateForm] = useState({firstName: "", lastName: "", email: ""});
    const [editForm, setEditForm] = useState({id: "", firstName: "", lastName: "", email: ""});
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [createError, setCreateError] = useState("");
    const [createApiError, setCreateApiError] = useState({
        firstName: "",
        lastName: "",
        email: "",
        general: "",
    });
    const [editApiError, setEditApiError] = useState({
        firstName: "",
        lastName: "",
        email: "",
        general: "",
    });
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const isSmall = useMediaQuery("(max-width:600px)");


    const variables = useMemo(
        () => ({
            pagination: {page, itemsPerPage: rowsPerPage},
            sortBy: sortFields,
            criteria: {
                deleted: false,
                ...(debouncedFilters.name ? {name: debouncedFilters.name} : {}),
                ...(debouncedFilters.email ? {email: debouncedFilters.email} : {}),
            }
        }),
        [page, rowsPerPage, debouncedFilters]
    );

    const {data, loading, error, refetch} = useQuery(PAGINATE_USERS, {
        variables,
        fetchPolicy: "no-cache",
    });

    const [createUser, {loading: creating}] = useMutation(CREATE_USER);
    const [updateUser, {loading: updating}] = useMutation(UPDATE_USER);
    const [deleteUser, {loading: deleting}] = useMutation(DELETE_USER);

    const pageInfo = data?.paginateUser?.pageInfo;
    const rows = data?.paginateUser?.content ?? [];
    const currentPage = pageInfo?.pageNumber ?? page;

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({
                name: nameFilter.trim(),
                email: emailFilter.trim(),
            });
            setPage(0);
        }
    };

    const applyFilters = () => {
        setDebouncedFilters({
            name: nameFilter.trim(),
            email: emailFilter.trim(),
        });
        setPage(0);
    };

    const openEdit = (row) => {
        setEditForm({id: row.id, firstName: row.firstName, lastName: row.lastName, email: row.email});
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);

        setEditForm((prev) => ({...prev, firstName: row.firstName, lastName: row.lastName}));
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({email: "", firstName: "", lastName: "", general: ""});
        setCreateForm({firstName: "", lastName: "", email: ""});
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({email: "", firstName: "", lastName: "", general: ""});
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({email: "", firstName: "", lastName: "", general: ""});
        if (!createForm.firstName.trim() || !createForm.lastName.trim() || !createForm.email.trim()) {
            setCreateError(t("validation.requiredAll"));
            return;
        }
        if (!isValidEmail(createForm.email)) {
            setCreateError(t("validation.invalidEmail"));
            return;
        }

        try {
            setCreateError("");
            const {data} = await createUser({variables: {input: createForm}});
            const created = data?.createUser;
            handleCloseCreate();
            setCreateForm({firstName: "", lastName: "", email: ""});

            if (created) {
                setCreatedUserInfo({
                    title: t("userPage.success.title"),
                    fields: [
                        {label: t("userPage.success.fields.name"), value: `${created.firstName} ${created.lastName}`},
                        {label: t("userPage.success.fields.email"), value: created.email},
                        {label: t("userPage.success.fields.defaultPassword"), value: created.defaultPassword, color: "green"},
                    ],
                    copyText: `${created.defaultPassword}`,
                    showAlert: true,
                    alertText: t("userPage.success.alertText"),
                });
            }
            setSnackbar({open: true, message: t("userPage.messages.created"), severity: "success"});
            refetch();
        } catch (error) {
            handleGraphQLError(error, setCreateApiError, t);

        }
    };

    const doUpdate = async () => {
        setEditApiError({email: "", firstName: "", lastName: "", general: ""});
        const {id, ...input} = editForm;
        try {
            if (!editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()) {
                setEditError(t("validation.requiredAll"));
                return;
            }
            if (!isValidEmail(editForm.email)) {
                setEditError(t("validation.invalidEmail"));
                return;
            }
            setEditError("");
            await updateUser({variables: {id, input}});
            handleCloseEdit();

            setSnackbar({open: true, message: t("userPage.messages.updated"), severity: "success"});
            refetch();
        } catch (error) {
            handleGraphQLError(error, setEditApiError, t);
        }
    };


    const doDelete = async () => {
        try {
            const userId =
                localStorage.getItem("id") ||
                sessionStorage.getItem("id");
            if (userId === deleteId) {
                setDeleteError(t("common.cannotDeleteYourself") || "You cannot delete your own account.");
                return;
            }
            setDeleteError("");
            await deleteUser({variables: {id: deleteId}});
            handleCloseDelete();

            setSnackbar({open: true, message: t("userPage.messages.deleted"), severity: "success"});
            refetch();
        } catch (error) {
            const msg = error?.graphQLErrors?.[0]?.message || t("common.deleteFailed");
            setDeleteError(msg);
        }
    };


    useEffect(() => {
        if (!createOpen) setCreateError("");
    }, [createOpen]);
    useEffect(() => {
        if (!editOpen) setEditError("");
    }, [editOpen]);
    useEffect(() => {
        if (!deleteOpen) setDeleteError("");
    }, [deleteOpen]);


    const onResetFilter = () => {
        setNameFilter("");
        setPage(0);
    };

    const onRefresh = () => {
        refetch();
    };

    return (
        <Box>
            {/* <Typography variant="h5" gutterBottom>{t("userPage.title")}</Typography> */}
            <PageHeader
                title={t("userPage.title")}
                subtitle={t("userPage.subtitle")}
                icon={<SupervisorAccountIcon/>}
            />

            <Paper
                elevation={3}
                sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >

                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >


                    <SearchField
                        label={t("userPage.searchNamePlaceholder")}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("userPage.searchNamePlaceholder")}
                        isSmall={isSmall}
                    />

                    <SearchField
                        label={t("userPage.searchEmailPlaceholder")}
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("userPage.searchEmailPlaceholder")}
                        isSmall={isSmall}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon/>}
                        onClick={applyFilters}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon/>}
                        onClick={() => {
                            setNameFilter("");
                            setEmailFilter("");
                            setDebouncedFilters({name: "", email: ""});
                            setPage(0);
                        }}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.clear")}
                    </Button>

                    <Box sx={{flex: 1}}/>


                    <Tooltip title={t("common.refresh")}>
                        <span>
                            <IconButton onClick={onRefresh} aria-label="refresh">
                                <RefreshIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => setCreateOpen(true)}
                    >
                        {t("userPage.createButton")}
                    </Button>
                </Toolbar>


                {loading ? (
                    <Box sx={{p: 4, textAlign: "center"}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Box sx={{p: 2}}>
                        <Alert severity="error">{error.message}</Alert>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{maxHeight: 520}}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <EllipsisCell
                                            sx={{
                                                bgcolor: "grey.50",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderBottom: "2px solid",
                                                borderBottomColor: "divider",
                                                color: "text.primary"
                                            }}
                                        >
                                            <TableSortLabel
                                                {...getSortProps("firstName")}
                                                onClick={() => handleSort("firstName")}
                                            >
                                                {t("userPage.table.name")}
                                            </TableSortLabel>
                                        </EllipsisCell>
                                        <EllipsisCell
                                            sx={{
                                                bgcolor: "grey.50",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderBottom: "2px solid",
                                                borderBottomColor: "divider",
                                                color: "text.primary"
                                            }}
                                        >

                                            <TableSortLabel
                                                {...getSortProps("email")}

                                                onClick={() => handleSort("email")}
                                            >
                                                {t("userPage.table.email")}
                                            </TableSortLabel>
                                        </EllipsisCell>
                                        <EllipsisCell
                                            sx={{
                                                bgcolor: "grey.50",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderBottom: "2px solid",
                                                borderBottomColor: "divider",
                                                color: "text.primary",
                                                width: "1%",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            <TableSortLabel
                                                {...getSortProps("updatedTimestamp")}
                                                onClick={() => handleSort("updatedTimestamp")}
                                            >
                                                {t("userPage.table.updated")}
                                            </TableSortLabel>
                                        </EllipsisCell>
                                        <EllipsisCell
                                            sx={{
                                                bgcolor: "grey.50",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderBottom: "2px solid",
                                                borderBottomColor: "divider",
                                                color: "text.primary",
                                                width: "1%",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {t("userPage.table.actions")}
                                        </EllipsisCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <EllipsisCell colSpan={4} align="center">{t("userPage.noRecords")}</EllipsisCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{
                                                    "&:nth-of-type(even)": {
                                                        bgcolor: "grey.50"
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "action.hover"
                                                    }
                                                }}
                                            >
                                                <EllipsisCell>
                                                    {`${r.firstName} ${r.lastName}`}
                                                </EllipsisCell>
                                                <EllipsisCell>
                                                    {r.email}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    {r.updatedTimestamp
                                                        ? formatToLocalTime(r.updatedTimestamp)
                                                        : "-"}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    <Tooltip title={t("common.update")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(r)}
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => openDelete(r)}
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </EllipsisCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            labelRowsPerPage={t("common.rowsPerPage")}
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={pageInfo?.totalElements ?? 0}
                            rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </>
                )}
            </Paper>


            <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.createTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {createApiError.general && (
                    <Alert severity="error" sx={{mb: 2}}>{createApiError.general}</Alert>
                )}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("userPage.fields.firstName")}
                            value={createForm.firstName}
                            onChange={(e) => setCreateForm({...createForm, firstName: e.target.value})}
                            error={createApiError.firstName || (createError && !createForm.firstName.trim())}
                            helperText={createApiError.firstName || (
                                createError && !createForm.firstName.trim()
                                    ? t("validation.requiredField")
                                    : ""
                            )
                            }
                            autoFocus
                        />
                        <TextField
                            label={t("userPage.fields.lastName")}
                            value={createForm.lastName}
                            onChange={(e) => setCreateForm({...createForm, lastName: e.target.value})}
                            error={createApiError.lastName || (createError && !createForm.lastName.trim())}
                            helperText={
                                createApiError.lastName || (
                                    createError && !createForm.lastName.trim()
                                        ? t("validation.requiredField")
                                        : "")
                            }
                        />
                        <TextField
                            label={t("userPage.fields.email")}
                            type="email"
                            value={createForm.email}
                            onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                            error={createApiError.email || (
                                createError &&
                                (!createForm.email.trim() || !isValidEmail(createForm.email))
                            )
                            }
                            helperText={
                                createApiError.email || (createError && !createForm.email.trim()
                                        ? t("validation.requiredField")
                                        : createError && !isValidEmail(createForm.email)
                                            ? t("validation.invalidEmail")
                                            : ""
                                )
                            }
                        />

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                    <Button
                        onClick={doCreate}
                        disabled={creating}
                        aria-busy={creating}
                        variant="contained"
                    >
                        {creating ? <CircularProgress size={20}/> : t("common.create")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.updateTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {editApiError.general && (
                    <Alert severity="error" sx={{mb: 2}}>{editApiError.general}</Alert>
                )}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("userPage.fields.firstName")}
                            value={editForm.firstName}
                            onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                            error={editApiError.firstName || (editError && !editForm.firstName.trim())}
                            helperText={editApiError.firstName || (
                                editError && !editForm.firstName.trim()
                                    ? t("validation.requiredField")
                                    : ""
                            )
                            }
                            autoFocus
                        />
                        <TextField
                            label={t("userPage.fields.lastName")}
                            value={editForm.lastName}
                            onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                            error={editApiError.lastName || (editError && !editForm.lastName.trim())}
                            helperText={editApiError.lastName || (
                                editError && !editForm.lastName.trim()
                                    ? t("validation.requiredField")
                                    : ""
                            )}
                        />
                        <TextField
                            label={t("userPage.fields.email")}
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                            error={editApiError.email || (
                                editError &&
                                (!editForm.email.trim() || !isValidEmail(editForm.email)))
                            }
                            helperText={editApiError.email || (
                                editError && !editForm.email.trim()
                                    ? t("validation.requiredField")
                                    : editError && !isValidEmail(editForm.email)
                                        ? t("validation.invalidEmail")
                                        : ""
                            )
                            }
                        />

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                    <Button
                        onClick={doUpdate}
                        disabled={updating}
                        aria-busy={updating}
                        variant="contained"
                    >
                        {updating ? <CircularProgress size={20}/> : t("common.update")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.deleteConfirmTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {deleteError && (
                    <Alert severity="error" sx={{mb: 2}}>{deleteError}</Alert>
                )}
                <DialogContent>
                    <Typography variant="h6" sx={{color: "#d32f2f", fontWeight: "bold", mt: 1}}>
                        {`${editForm.firstName} ${editForm.lastName}`}
                    </Typography>
                    <Typography>{t("userPage.deleteConfirmMessage")}</Typography>

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                    <Button onClick={doDelete} color="error" disabled={deleting} aria-busy={deleting} variant="contained">
                        {deleting ? <CircularProgress size={20}/> : t("common.delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessDialog
                open={!!createdUserInfo}
                onClose={() => setCreatedUserInfo(null)}
                info={createdUserInfo}
            />


            <Snackbar
                TransitionComponent={SlideUp}
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                sx={{
                    "& .MuiAlert-root": {
                        borderRadius: 2,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                        fontWeight: 500,
                        minWidth: 260,
                        justifyContent: "center",
                    },
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default Users;
